import '@theme/variant-picker';

// A small guard around Horizon's existing product/cart implementation.
// It never submits a cart request itself or queues an unconfirmed variant.
class SonderSizeChoice extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    queueMicrotask(() => this.initialize());
  }

  initialize() {
    if (!this.isConnected || this.controller.signal.aborted) return;
    const { signal } = this.controller;
    this.formRoot = this.closest('product-form-component');
    this.scope = this.closest('#quick-add-modal-content') || this.closest('.product-details');
    this.picker = this.scope?.querySelector('variant-picker[data-product-id="' + this.dataset.productId + '"]');
    if (!this.formRoot || !this.picker) return;
    this.chosenVariant = undefined;
    this.resolvedVariant = undefined;
    this.available = false;
    this.sizeInputs().forEach(input => {
      input.checked = false;
      input.removeAttribute('checked');
      input.dataset.currentChecked = 'false';
      input.dataset.previousChecked = 'false';
    });
    this.scope.addEventListener('change', event => {
      if (!event.target.matches('variant-picker input[type="radio"]')) return;
      if (!event.target.matches('variant-picker [data-option-name="size"] input[type="radio"]')) {
        // A colour change invalidates the previous colour's variant ID.
        // Require a fresh size choice instead of waiting for that old ID forever.
        this.chosenVariant = undefined;
        this.resolvedVariant = undefined;
        this.available = false;
        this.sync();
        return;
      }
      this.chosenVariant = event.target.dataset.variantId;
      this.resolvedVariant = undefined;
      this.available = false;
      this.sync();
    }, { signal, capture: true });
    this.scope.addEventListener('variant:update', event => {
      if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
      this.resolvedVariant = String(event.detail.resource?.id || '');
      this.available = event.detail.resource?.available === true;
      // Horizon updates the form and morphs its button later in the same event dispatch.
      queueMicrotask(() => {
        if (signal.aborted) return;
        // The server deliberately renders no size as checked. Restore only the
        // shopper's explicit choice after Horizon replaces the picker markup.
        this.sizeInputs().forEach(input => {
          const selected = !!this.chosenVariant && input.dataset.variantId === this.chosenVariant;
          input.checked = selected;
          input.dataset.currentChecked = String(selected);
        });
        this.enableSizeInputs();
        this.sync();
      });
    }, { signal });
    this.formRoot.addEventListener('submit', event => {
      if (this.ready) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.focusSize();
    }, { signal, capture: true });
    this.formRoot.addEventListener('click', event => {
      if (!event.target.closest('button[type="submit"]') || this.ready) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.focusSize();
    }, { signal, capture: true });
    window.addEventListener('pageshow', () => this.sync(), { signal });
    this.enableSizeInputs();
    this.sync();
  }

  enableSizeInputs() {
    this.sizeInputs().forEach(input => {
      if (!input.hasAttribute('data-sonder-size-pending')) return;
      input.disabled = false;
      input.removeAttribute('data-sonder-size-pending');
    });
  }

  sizeInputs() { return [...this.picker.querySelectorAll('[data-option-name="size"] input[type="radio"]')]; }

  get ready() {
    return !!this.chosenVariant && this.available && this.resolvedVariant === this.chosenVariant &&
      this.formRoot?.querySelector('input[name="id"]')?.value === this.chosenVariant;
  }

  sync() {
    if (!this.formRoot) return;
    const button = this.formRoot.querySelector('button[ref="addToCartButton"]');
    if (!button) return;
    const waiting = this.chosenVariant && this.resolvedVariant !== this.chosenVariant;
    const label = this.ready ? 'Add to cart' : !this.chosenVariant ? 'Choose a size' : waiting ? 'Updating size…' : 'Size unavailable';
    button.disabled = !this.ready;
    const text = button.querySelector('.add-to-cart-text__content > span > span');
    if (text) text.textContent = label;
    this.dataset.ready = String(this.ready);
    this.querySelector('[data-size-status]').textContent = this.ready ? 'Selected size: ' + (this.picker.querySelector('[data-option-name="size"] input:checked')?.value || '') : !this.chosenVariant ? 'Choose a size above to continue.' : waiting ? 'Updating your selected size…' : 'This size is unavailable. Choose another size.';
    const sticky = document.querySelector('sticky-add-to-cart[data-product-id="' + this.dataset.productId + '"] [data-sonder-sticky-label]');
    if (sticky && !this.closest('#quick-add-modal-content')) sticky.textContent = this.ready ? 'Add to cart' : 'Choose a size';
  }

  focusSize() {
    const input = this.picker?.querySelector('[data-option-name="size"] input:checked') || this.sizeInputs().find(input => input.dataset.optionAvailable !== 'false');
    if (!input) return;
    input.closest('fieldset').scrollIntoView({ block: 'center', behavior: 'auto' });
    input.focus({ preventScroll: true });
  }

  disconnectedCallback() { this.controller?.abort(); }
}
if (!customElements.get('sonder-size-choice')) customElements.define('sonder-size-choice', SonderSizeChoice);

// Plain anchors still work without JavaScript; keyboard users land at the garment heading.
document.addEventListener('click', event => {
  const link = event.target.closest('[data-brigade-jump]');
  if (!link) return;
  const target = document.getElementById(link.hash.slice(1));
  if (!target) return;
  event.preventDefault();
  history.replaceState(null, '', link.hash);
  target.scrollIntoView({ block: 'start', behavior: 'auto' });
  target.querySelector('h2')?.focus({ preventScroll: true });
});
